"use client";

import React, { useEffect, useRef, useState } from "react";
import ImageWrapper from "../ImageWrapper";

interface ModelSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string; isDisabled?: boolean }[];
  placeholder?: string;
  brandName?: string;
  showCrossIcon?: boolean;
  emptyText?: string;
}

const ModelSelect: React.FC<ModelSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Select Model",
  brandName = "",
  showCrossIcon = false,
  emptyText = "No results found",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options?.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const selectedLabel = options.find((opt) => opt.value.toLowerCase() === value.toLowerCase())?.label || "";
  const displayedValue = selectedLabel ? `${brandName || ""} ${selectedLabel}` : placeholder;

  const selectId = `${label.toLowerCase().replace(/\s+/g, "-")}-select`;

  return (
    <div
      ref={dropdownRef}
      className="relative">
      <label htmlFor={selectId} className="font-[Roboto] font-semibold text-[13px] capitalize text-black mb-4">{label}</label>
      <button
        id={selectId}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 text-left font-medium text-sm transition-all duration-200 outline-none focus:outline-none focus:ring-0 bg-white">
        <span className={`line-clamp-1 ${selectedLabel ? "text-gray-900" : "text-gray-500"}`}>{displayedValue}</span>

        <div className="flex items-center gap-1.5 ml-2 shrink-0">
          {showCrossIcon && value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  onChange("");
                }
              }}
              className="p-0.5 hover:bg-gray-100 rounded-full transition-colors duration-200 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600"
              title="Clear selection"
              aria-label="Clear selection"
            >
               <ImageWrapper
                  src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`} 
                  alt="Clear search"
                  width={14} 
                  height={14}
                  className="invert-[60%] brightness-[70%]"
                />
           
            </span>
          )}

          <ImageWrapper
            src="/drop-arrow.svg"
            alt="Dropdown Icon"
            title="Dropdown Icon"
            width={8}
            height={8}
            cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}
            className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50 p-[8px] md:p-[14px] ">
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full pl-10 pr-4 py-2 focus:outline-none bg-[#F3F3F5] rounded-md"
              autoFocus
            />

            <span className="absolute left-3 top-3.5 w-5 h-5 text-gray-400">
              <ImageWrapper
                src={"/search.svg"}
                alt="Search"
                title="Search"
                width={18}
                height={18}
                cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}
              />
            </span>
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = value === opt.value;
                const isDisabled = opt.isDisabled && !isSelected;
                return (
                  <li
                    key={opt.value}
                    onMouseDown={(e) => {
                      if(isDisabled) {
                        e.preventDefault();
                        return;
                      }
                      e.preventDefault();
                      onChange(opt.value);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    className={`px-2 md:px-4 py-2 border-b-[0.5px] border-gray-200 font-[Roboto] font-normal text-[14px] leading-[24px]
                    ${
                      isDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-[#008C8B]/20 cursor-pointer text-[#0A0A0A]"
                    }
                    ${isSelected ? "bg-[#008C8B]/10 font-medium text-[#008C8B]" : ""}
                  `}>
                    {opt.label}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-3 text-gray-500">{emptyText}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ModelSelect;
